import { createContext, useContext } from 'react';
import { reportBillingProblem } from '@/lib/api/billingProblems';
import { env } from '@/app/env';
import { getAccessToken } from '@/lib/auth/accessToken';
import { ApiError, type ProblemDetails } from '@/lib/api/errors';

export interface ApiRequestInit extends Omit<RequestInit, 'body'> {
  json?: unknown;
  query?: Record<string, string | number | boolean | undefined | null>;
  anonymous?: boolean;
}

export type GetToken = () => Promise<string | undefined>;

export interface ApiClient {
  get: <T>(path: string, init?: ApiRequestInit) => Promise<T>;
  post: <T>(path: string, json?: unknown, init?: ApiRequestInit) => Promise<T>;
  patch: <T>(path: string, json?: unknown, init?: ApiRequestInit) => Promise<T>;
  put: <T>(path: string, json?: unknown, init?: ApiRequestInit) => Promise<T>;
  delete: <T>(path: string, init?: ApiRequestInit) => Promise<T>;
}

function ensureTrailingSlash(u: string): string {
  return u.endsWith('/') ? u : `${u}/`;
}

function buildUrl(baseUrl: string, path: string, query?: ApiRequestInit['query']): string {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  if (!baseUrl) {
    const qs = new URLSearchParams();
    if (query) {
      for (const [k, v] of Object.entries(query)) {
        if (v === undefined || v === null) continue;
        qs.set(k, String(v));
      }
    }
    const tail = qs.toString();
    return tail ? `${normalizedPath}?${tail}` : normalizedPath;
  }
  const url = new URL(normalizedPath.slice(1), ensureTrailingSlash(baseUrl));
  if (query) {
    for (const [k, v] of Object.entries(query)) {
      if (v === undefined || v === null) continue;
      url.searchParams.set(k, String(v));
    }
  }
  return url.toString();
}

export async function apiFetch<T = unknown>(
  getToken: GetToken,
  path: string,
  init: ApiRequestInit = {},
): Promise<T> {
  const { json, query, anonymous, headers, ...rest } = init;

  const finalHeaders = new Headers(headers);
  finalHeaders.set('Accept', 'application/json');

  let body: BodyInit | undefined;
  if (json !== undefined) {
    finalHeaders.set('Content-Type', 'application/json');
    body = JSON.stringify(json);
  }

  if (!anonymous) {
    const token = await getToken();
    // A protected request without a bearer is a guaranteed 401 that
    // masquerades as a server-side auth problem — fail it here instead.
    if (!token) throw new Error('Not signed in.');
    finalHeaders.set('Authorization', `Bearer ${token}`);
  }

  const res = await fetch(buildUrl(env.apiBaseUrl, path, query), {
    ...rest,
    headers: finalHeaders,
    body,
  });

  if (res.status === 204 || res.headers.get('content-length') === '0') {
    if (!res.ok) throw new ApiError(res.status, res.statusText || `HTTP ${res.status}`);
    return undefined as T;
  }

  const contentType = res.headers.get('content-type') ?? '';
  const isJson = contentType.includes('application/json') || contentType.includes('problem+json');
  const payload: unknown = isJson ? await res.json().catch(() => undefined) : await res.text();

  if (!res.ok) {
    const problem = isJson ? (payload as ProblemDetails) : undefined;
    // || not ?? — statusText is the empty string over HTTP/2, and an
    // empty message renders as a blank alert in QueryView.
    const text =
      !isJson && typeof payload === 'string' && payload.trim() ? payload.trim().slice(0, 200) : '';
    const message =
      problem?.title || problem?.detail || text || res.statusText || `HTTP ${res.status}`;
    reportBillingProblem(res.status, problem);
    throw new ApiError(res.status, message, problem);
  }

  return payload as T;
}

export function createApiClient(getToken: GetToken): ApiClient {
  return {
    get: (path, init) => apiFetch(getToken, path, { ...init, method: 'GET' }),
    post: (path, json, init) => apiFetch(getToken, path, { ...init, method: 'POST', json }),
    patch: (path, json, init) => apiFetch(getToken, path, { ...init, method: 'PATCH', json }),
    put: (path, json, init) => apiFetch(getToken, path, { ...init, method: 'PUT', json }),
    delete: (path, init) => apiFetch(getToken, path, { ...init, method: 'DELETE' }),
  };
}

function isMissingLocalUser(err: unknown): boolean {
  return (
    err instanceof ApiError && err.status === 401 && err.problem?.code === 'missing-local-user'
  );
}

// Single-flight: on first-ever sign-in many feature queries race the
// /v1/me call that creates the platform.users row, and each loser gets
// a 401 missing-local-user. They all await the same provisioning
// round-trip, then retry once.
let provisioning: Promise<void> | null = null;

function provisionLocalUser(getToken: GetToken): Promise<void> {
  provisioning ??= apiFetch<unknown>(getToken, '/v1/me')
    .then(() => undefined)
    .finally(() => {
      provisioning = null;
    });
  return provisioning;
}

// The client reads the session at call time, so one instance serves every
// consumer. Rebuilding it on each auth-state change re-ran every consumer on
// every refresh attempt, which turned one failed refresh into a request loop.
const sessionClient: ApiClient = (() => {
  const raw = createApiClient(getAccessToken);

  // Server 401s deliberately do NOT signal session expiry. Every
  // protected request here carries a token the SDK just refreshed or
  // validated, so a 401 means the server rejected a valid grant
  // (audience misconfig, revocation lag) — redirecting through the shell
  // can't fix that and would loop. Dead sessions are caught where they
  // actually surface: terminal errors in getAccessToken.
  const run = async <T>(call: () => Promise<T>): Promise<T> => {
    try {
      return await call();
    } catch (err) {
      if (isMissingLocalUser(err)) {
        try {
          await provisionLocalUser(getAccessToken);
        } catch {
          throw err;
        }
        return await call();
      }
      throw err;
    }
  };

  return {
    get: <T>(path: string, init?: ApiRequestInit) => run<T>(() => raw.get<T>(path, init)),
    post: <T>(path: string, json?: unknown, init?: ApiRequestInit) =>
      run<T>(() => raw.post<T>(path, json, init)),
    patch: <T>(path: string, json?: unknown, init?: ApiRequestInit) =>
      run<T>(() => raw.patch<T>(path, json, init)),
    put: <T>(path: string, json?: unknown, init?: ApiRequestInit) =>
      run<T>(() => raw.put<T>(path, json, init)),
    delete: <T>(path: string, init?: ApiRequestInit) => run<T>(() => raw.delete<T>(path, init)),
  };
})();

export const ApiClientContext = createContext<ApiClient | null>(null);
export const useApi = (): ApiClient => useContext(ApiClientContext) ?? sessionClient;
